import React, { useEffect, useMemo, useState } from "react";
import API from "../../../util/API";
import { Badge, Button, Card, Field, Input } from "./mailUI";

const TestMail = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [variables, setVariables] = useState({});
  const [to, setTo] = useState("");
  const [status, setStatus] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.key === selectedKey) || null,
    [templates, selectedKey]
  );

  const loadTemplates = async () => {
    try {
      const res = await API.get("/mail/templates");
      const items = res.data?.data?.items || [];
      setTemplates(items);
      if (!selectedKey && items.length) setSelectedKey(items[0].key);
    } catch {
      setTemplates([]);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!selectedTemplate) return;
    const next = {};
    (selectedTemplate.variables || []).forEach((v) => {
      next[v] = variables[v] || "";
    });
    setVariables(next);
  }, [selectedKey]);

  const handleSend = async () => {
    setLoading(true);
    setStatus({ type: "", text: "" });
    try {
      await API.post(`/mail/templates/${selectedKey}/test`, { to, variables });
      setStatus({ type: "success", text: "Template email sent successfully." });
    } catch (err) {
      setStatus({
        type: "error",
        text:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to send template email",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="Test Mail"
      subtitle="Select a template, fill variables, and send a test email."
      right={<Badge tone="info">{templates.length} templates</Badge>}
    >
      <div className="space-y-4">
        <Field label="Template">
          <select
            value={selectedKey}
            onChange={(event) => setSelectedKey(event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900"
          >
            {templates.map((tpl) => (
              <option key={tpl.key} value={tpl.key}>
                {tpl.group} - {tpl.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Send To">
          <Input value={to} onChange={(event) => setTo(event.target.value)} placeholder="email@company.com" />
        </Field>

        {(selectedTemplate?.variables || []).length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {selectedTemplate.variables.map((variable) => (
              <Field key={variable} label={variable}>
                <Input
                  value={variables[variable] || ""}
                  onChange={(event) =>
                    setVariables((prev) => ({ ...prev, [variable]: event.target.value }))
                  }
                  placeholder={`Value for ${variable}`}
                />
              </Field>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">This template has no variables.</p>
        )}

        <Button onClick={handleSend} disabled={loading || !selectedKey || !to}>
          {loading ? "Sending..." : "Send Test Email"}
        </Button>
        {status.text ? (
          <p
            className={`text-sm ${status.type === "success" ? "text-emerald-600" : "text-rose-600"}`}
          >
            {status.text}
          </p>
        ) : null}
      </div>
    </Card>
  );
};

export default TestMail;
